import { ModalContext } from 'app/app';
import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import {
  Animate,
  Grid,
  Search,
  Table,
  ViewContext,
} from 'components/lib';
import { useContext, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import InputUtils from '../../helpers/inputUtils';
import { IconedButton } from "../../components/IconedButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import DateHelper from "../../helpers/dateUtils";
import { ChevronLeft, ChevronRight } from 'feather-icons-react/build/IconComponents';


const inputUtils = new InputUtils();

export function RequestedBind(props) {
  const { setModal } = useContext(ModalContext);
  const navigate = useNavigate();
  const dateFormatter = new DateHelper();
  const [payload, setPayload] = useState();
  const [loading, setLoading] = useState(false);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [agencyFilter, setAgencyFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [bindRequests, setBindRequests] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  const [page, setPage] = useState(initialPage);

  const context = useContext(ViewContext);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  async function fetchFiltersData() {
    setLoading(true);

    await axios
      .get('/api/bind-requests/admin/search', {
        params: {
          page: 1,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          const bindRequests = response.data.data;

          const uniqueAgencies = new Map();
          const uniqueCompanies = new Map();

          bindRequests.forEach((bindRequest) => {
            if (bindRequest.agencyId && bindRequest.agencyName) {
              uniqueAgencies.set(bindRequest.agencyId, {
                label: bindRequest.agencyName,
                value: bindRequest.agencyId,
              });
            }

            if (bindRequest.companyName) {
              uniqueCompanies.set(bindRequest.companyName, {
                label: inputUtils.capitalizeFirstLetter(bindRequest.companyName).replaceAll('Llc', 'LLC'),
                value: bindRequest.companyName,
              });
            }
          });

          setAgenciesOptions(Array.from(uniqueAgencies.values()));
          setCompaniesOptions(Array.from(uniqueCompanies.values()));
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load agencies and companies',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchFiltersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchBindRequests(page, search, statusFilter, agencyFilter, companyFilter, pageSize) {
    setLoading(true);
    setSearchParams({ page });

    await axios
      .get('/api/bind-requests/admin/search', {
        params: {
          page: page,
          search: search,
          status: statusFilter,
          agencyName: agencyFilter.length > 0 ? agencyFilter.join(',') : undefined,
          companyName: companyFilter.length > 0 ? companyFilter.join(',') : undefined,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          pageSize
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          setPayload(response.data);
          setBindRequests(
            response.data.data.map((bindRequest) => ({
              id: bindRequest.id,
              Number: bindRequest.bindNumber,
              'Agency name': bindRequest.agencyName || 'N/A',
              'Company Name': bindRequest.companyName || 'N/A',
              'Submission Number': bindRequest.submissionNumber ? (
                bindRequest.submissionNumber
              ) : (
                bindRequest.id
              ),
              'Created At': bindRequest.createdAt && dateFormatter.timestamp(bindRequest.createdAt),
              'Updated At': bindRequest.updatedAt && dateFormatter.timestamp(bindRequest.updatedAt),
            }))
          );
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load bind requests',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchBindRequests(
      page,
      searchQueryString,
      statusFilter,
      agencyFilter,
      companyFilter,
      pageSize
    ).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQueryString, statusFilter, agencyFilter, companyFilter, pageSize]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchQueryString(searchQuery);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          {['Active', 'Bound', 'Declined', 'Not Taken Up'].map((status) => {
            return (
              <div
                key={status}
                style={{
                  marginLeft: '5px',
                  width: '150px',
                  backgroundColor:
                    statusFilter === status ? 'black' : '#EAEAEA',
                  color:
                    statusFilter === status ? 'white' : '#090E3480',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'pointer',
                }}
                onClick={() => handleStatusFilter(status)}
              >
                {status}
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconedButton
            size="200px"
            color="#d32425"
            textColor="white"
            icon="plus-square"
            text="Create Bind Ticket"
            marginRight
            action={async () => {
              setModal({
                name: 'bind_request_modal',
                action: () => {
                },
              });
            }}
          />
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper} style={{ marginTop: 0 }}>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '60%', marginTop: 30 }}>
              <Search
                callback={(query) => {
                  setSearchQuery(query);
                }}
              />
            </div>
            <div style={{ marginLeft: 20, width: 400 }}>
              <FieldInput
                id="agency_filter"
                type="multi_select"
                label="Filter by agency"
                isDisabled={loading}
                defaultValue={[]}
                handleChange={(agency) => {
                  setAgencyFilter(agency.map((agency) => {
                    return agency.label
                  }));
                }}
                options={agenciesOptions}
              />
            </div>
            <div style={{ marginLeft: 20, width: 400 }}>
              <FieldInput
                id="company_filter"
                type="multi_select"
                label="Filter by company"
                isDisabled={loading}
                defaultValue={[]}
                handleChange={(company) => {
                  setCompanyFilter(
                    company.map((company) => {
                      return company.value;
                    })
                  );
                }}
                options={companiesOptions}
              />
            </div>
          </div>
          <Table
            data={bindRequests}
            hide={['id', 'dot_number', 'status']}
            loading={loading}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => {
                    navigate(
                      `/admin/requestedbind/${row.id}?page=${page}`
                    );
                  },
                }
              ],
            }}
          />
          {payload && !loading && (
            <div className="flex items-center justify-between mb-2 mt-6 sm:mb-0">
              <div className="flex items-center">
                <span className="mr-2">Items per page</span>
                <select
                  className="
                  border bg-white border-gray-300
                  rounded-md shadow-sm p-2"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {[10, 20, 30, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="flex items-center">
                  <span className="mr-1">page</span>
                  <input
                    type="number"
                    min={1}
                    max={payload.pages}
                    defaultValue={page}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const newPage = e.target.value ? Number(e.target.value) : 1;
                        setPage(newPage);
                      }
                    }}
                    className="
                      w-16 p-2 rounded-xl border
                      border-gray-100 text-center"
                  />
                  <span className="ml-1">of {payload.pages}</span>
                </span>

                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.min(prev + 1, payload.pages))}
                  disabled={page === payload.pages}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="flex items-center">
                <span>Total items: {payload.total}</span>
              </div>
            </div>
          )}
        </div>
      </Grid>
    </Animate>
  );
}
